/**
 * Reference Store fixtures — hand-authored normalized signals that stand
 * in for real DemandProvider / VisibilityProvider output.
 *
 * These fixtures are deliberately generic (product/service terms, no
 * shop-type templating). They only exist so the harness can exercise the
 * reasoning engine deterministically, without vendor calls.
 *
 * The reasoning engine must NEVER read from this file.
 */

import type { BusinessContext } from "../types";
import type { DemandSignal } from "../providers/demand";
import type { VisibilitySignal } from "../providers/visibility";

export interface ReferenceStoreFixture {
  slug: string;
  business_context: BusinessContext;
  demand: DemandSignal[];
  visibility: VisibilitySignal[];
  provider_versions: Record<string, string>;
}

const commonProviders = { "mock-demand": "0.1.0", "mock-visibility": "0.1.0" };

/** Reference Store A — plant shop (shop type is context, not template). */
export const referenceStoreA: ReferenceStoreFixture = {
  slug: "reference-store-a",
  business_context: {
    business_id: "fixture-a",
    strengths: [
      "Deep expertise in houseplants and plant care",
      "In-store workshops and community events",
      "Curated selection of unusual/specialty plants",
    ],
    goals: [
      "Get more locals through the door",
      "Grow workshop attendance",
      "Reduce wasted ad spend",
    ],
    constraints: [
      "Small team — limited capacity for new services",
      "No dedicated marketing hire",
    ],
  },
  demand: [
    { term: "pet safe plants", monthly_volume: 90, trend: "rising", intent: "commercial", local_confidence: 0.9 },
    { term: "houseplants", monthly_volume: 85, trend: "flat", intent: "commercial", local_confidence: 0.8 },
    { term: "plant workshops", monthly_volume: 55, trend: "rising", intent: "commercial", local_confidence: 0.7 },
    { term: "rare plants", monthly_volume: 40, trend: "rising", intent: "commercial", local_confidence: 0.6 },
    { term: "plant care guide", monthly_volume: 30, trend: "flat", intent: "informational", local_confidence: 0.5 },
    { term: "fiddle leaf fig", monthly_volume: 20, trend: "falling", intent: "commercial", local_confidence: 0.5 },
  ],
  visibility: [
    { term: "pet safe plants", surface: "website", presence: "absent" },
    { term: "houseplants", surface: "website", presence: "thin", detail: "one paragraph on homepage, no category page" },
    { term: "plant workshops", surface: "website", presence: "present", detail: "events page exists but rarely updated" },
    { term: "rare plants", surface: "website", presence: "absent" },
    { term: "plant care guide", surface: "website", presence: "absent" },
    { term: "fiddle leaf fig", surface: "website", presence: "featured" },
  ],
  provider_versions: commonProviders,
};

/** Reference Store B — gift shop. */
export const referenceStoreB: ReferenceStoreFixture = {
  slug: "reference-store-b",
  business_context: {
    business_id: "fixture-b",
    strengths: [
      "Curated brand assortment",
      "Strong occasion-based merchandising",
      "Existing email list with engaged local audience",
    ],
    goals: [
      "Grow occasion-driven revenue",
      "Feature the right brands at the right time",
      "Own local gift discovery online",
    ],
    constraints: ["Limited time to produce weekly content"],
  },
  demand: [
    { term: "teacher gifts", monthly_volume: 75, trend: "rising", intent: "commercial", local_confidence: 0.8 },
    { term: "housewarming gifts", monthly_volume: 60, trend: "rising", intent: "commercial", local_confidence: 0.7 },
    { term: "nora fleming", monthly_volume: 50, trend: "flat", intent: "navigational", local_confidence: 0.7 },
    { term: "rifle paper co", monthly_volume: 45, trend: "flat", intent: "navigational", local_confidence: 0.6 },
    { term: "seasonal gift guide", monthly_volume: 35, trend: "rising", intent: "informational", local_confidence: 0.5 },
  ],
  visibility: [
    { term: "teacher gifts", surface: "website", presence: "absent" },
    { term: "housewarming gifts", surface: "website", presence: "thin", detail: "one product tag, no destination" },
    { term: "nora fleming", surface: "website", presence: "thin", detail: "brand carried but not featured" },
    { term: "rifle paper co", surface: "website", presence: "present" },
    { term: "seasonal gift guide", surface: "website", presence: "absent" },
  ],
  provider_versions: commonProviders,
};

/** Reference Store C — children's boutique with a hard no-discount constraint. */
export const referenceStoreC: ReferenceStoreFixture = {
  slug: "reference-store-c",
  business_context: {
    business_id: "fixture-c",
    strengths: [
      "Curated selection including Montessori-aligned toys",
      "Strong reputation for gift-giving occasions",
      "Personal service and local community trust",
    ],
    goals: [
      "Grow high-margin service revenue",
      "Own niches where no local competitor plays",
      "Attract parents planning ahead",
    ],
    constraints: [
      "Limited back-of-house capacity",
      "Owner protective of brand — no discount campaigns",
    ],
  },
  demand: [
    { term: "montessori toys", monthly_volume: 70, trend: "rising", intent: "commercial", local_confidence: 0.8 },
    { term: "kids birthday parties", monthly_volume: 65, trend: "rising", intent: "commercial", local_confidence: 0.9 },
    { term: "back to school lunch boxes", monthly_volume: 50, trend: "rising", intent: "commercial", local_confidence: 0.6 },
    { term: "personalized baby gifts", monthly_volume: 55, trend: "rising", intent: "commercial", local_confidence: 0.7 },
    // A tempting-but-forbidden signal — the engine must NOT recommend this.
    { term: "kids clothing discount sale", monthly_volume: 80, trend: "rising", intent: "commercial", local_confidence: 0.7 },
  ],
  visibility: [
    { term: "montessori toys", surface: "website", presence: "thin" },
    { term: "kids birthday parties", surface: "website", presence: "absent" },
    { term: "back to school lunch boxes", surface: "website", presence: "absent" },
    { term: "personalized baby gifts", surface: "website", presence: "absent" },
    { term: "kids clothing discount sale", surface: "website", presence: "absent" },
  ],
  provider_versions: commonProviders,
};

export const allFixtures: ReferenceStoreFixture[] = [
  referenceStoreA,
  referenceStoreB,
  referenceStoreC,
];